/* ============================================================
   Long Run Co. — Identity section renderer
   Consumes the identity data PUBLISHED by the portal JS
   (window.LRCMP_DATA + "lrc-mp:identity" event) — it does NOT
   make its own cp API calls. In the theme editor (design mode)
   it renders sample data so every setting is visible.
   ============================================================ */

/* First name, captured from the published identity payload by render() (in the
   renderer IIFE below) and read by LRCMPI.submitProductReview (in the LRCMPI IIFE).
   Declared at file scope so BOTH IIFEs share the same binding. */
var _firstName = "";
/* Whether the member has left a positive product review (member.hasReviewed). Set by the
   hydrate / on submit; read by LRCMPI.rate to swap the positive CTA for a thank-you. */
var _hasReviewed = false;

/* ---- LRCMPI: engagement-bar interactions (goal modal / cause / rate).
   UI only — every persist/save/award action is INERT. ---- */
window.LRCMPI = window.LRCMPI || (function () {
  function esc(s) { return String(s == null ? "" : s).replace(/[&<>"']/g, function (c) {
    return { "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c]; }); }
  function each(list, fn) { Array.prototype.forEach.call(list, fn); }

  // ---- cause persistence helpers (shared with the on-load hydrate below) ----
  // The pills carry no data-* value, so the canonical value is the trimmed
  // label text (the ".cn" span; falls back to the pill's own text).
  var storedCause = null;
  // Legacy label strings persisted before stable data-cause keys existed → key.
  var CAUSE_LEGACY = { "mental health":"mind", "veterans":"vets", "children":"body", "recovery":"recovery", "anxiety & depression":"mind", "addiction & recovery":"recovery", "body image":"body" };
  // Canonical value = the stable data-cause key. Fall back to legacy label mapping,
  // then to raw trimmed label text (so an unkeyed pill still yields *something*).
  function causeValue(el) {
    if (!el) return "";
    var key = el.getAttribute ? (el.getAttribute("data-cause") || "").trim() : "";
    if (key) return key;
    var label = el.querySelector ? el.querySelector(".cn") : null;
    var txt = String((label ? label.textContent : el.textContent) || "").trim();
    return CAUSE_LEGACY[txt.toLowerCase()] || txt;
  }
  // Normalize any stored value (new key, or a legacy label string) to a key for matching.
  function causeKeyOf(value) {
    var v = String(value == null ? "" : value).trim();
    if (!v) return "";
    return CAUSE_LEGACY[v.toLowerCase()] || v;
  }
  // Per-cause header icon — the EXACT SVGs used in the cause pills, keyed by
  // stable data-cause key. Swapped into the collapsed header's .hico so the
  // collapsed state reflects the member's chosen cause.
  var CAUSE_ICON = {
    mind: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round"><path d="M9.5 3a4 4 0 00-4 4 3.5 3.5 0 00-1 6.5V17a3 3 0 003 3h2.5V3z"/><path d="M14.5 3a4 4 0 014 4 3.5 3.5 0 011 6.5V17a3 3 0 01-3 3H12V3z"/></svg>',
    recovery: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round"><path d="M3 18h18"/><path d="M7 18a5 5 0 0110 0"/><path d="M12 3v3M5.5 8L4 6.5M18.5 8L20 6.5"/></svg>',
    body: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round"><path d="M12 21s-7-4.5-9.5-9A5 5 0 0112 5a5 5 0 019.5 7c-2.5 4.5-9.5 9-9.5 9z"/></svg>',
    vets: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round"><path d="M12 2l6 2.2V11c0 4.2-2.7 7.4-6 8.6C8.7 18.4 6 15.2 6 11V4.2z"/><path d="M9.4 11l1.8 1.8L15 9"/></svg>'
  };
  // Write the chosen cause's icon into the collapsed header (.hico) within the
  // same .hbar. No-op if the key has no icon (keeps the default heart).
  function setCauseHeaderIcon(scope, key) {
    if (!scope || !key || !CAUSE_ICON[key]) return;
    var bar = (scope.closest && scope.closest(".hbar")) || scope;
    var ico = bar.querySelector(".hico");
    if (ico) ico.innerHTML = CAUSE_ICON[key];
  }

  function selectCauseIn(scope, value) {
    if (!scope || !value) return false;
    var want = causeKeyOf(value);
    var matched = null;
    each(scope.querySelectorAll(".cause"), function (c) { if (causeValue(c) === want) matched = c; });
    if (!matched) return false;
    var wrap = matched.closest(".causes") || scope;
    each(wrap.querySelectorAll(".cause"), function (c) { c.classList.remove("sel"); });
    matched.classList.add("sel");
    setCauseHeaderIcon(matched, want);   // collapsed header reflects the choice
    return true;
  }
  function causeDesignMode(el) {
    if (typeof window.Shopify !== "undefined" && window.Shopify && window.Shopify.designMode === true) return true;
    var r = el && el.closest ? el.closest("[data-lrc-mp-identity]") : null;
    return !!(r && r.getAttribute("data-design-mode") === "true");
  }

  // ---- goal persistence (modal → save_goal → live .goalbar rebuild) ----
  // Modal pill token → canonical type (and back). Backend stores canonical.
  var GOAL_CANON = { event: "race", train: "consistency", none: "none" };
  var GOAL_REV = { race: "event", consistency: "train", none: "none" };
  var storedGoal = null;     // member's saved goal object (or null)
  var goalPickType = null;   // canonical type currently selected in the open modal
  var goalPickIcon = null;   // emoji chosen in the modal (NOT the captured bar icon)
  var goalIcon = null;       // captured bar icon — leading emoji, captured ONCE from the initial .gt span
  var goalEmptyHTML = null;  // original empty-state .goalbar innerHTML, captured ONCE
  var GOAL_ICONS = {
    peak:'<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><path d="M3 20l6-12 4 8 2.5-4L21 20z"/></svg>',
    route:'<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><circle cx="6" cy="6" r="2"/><circle cx="18" cy="18" r="2"/><path d="M8 6h5a3 3 0 010 6H9a3 3 0 000 6h7"/></svg>',
    flag:'<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><path d="M5 21V4"/><path d="M5 4h12l-2.5 4L17 12H5"/></svg>',
    watch:'<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="14" r="7"/><path d="M12 14v-4"/><path d="M10 2h4"/></svg>',
    medal:'<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="15" r="5"/><path d="M9 11L7 3M15 11l2-8"/></svg>',
    sunrise:'<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><path d="M3 18h18"/><path d="M8 18a4 4 0 018 0"/><path d="M12 4v3M5 9L4 8M19 9l1-1M2 14h2M20 14h2"/></svg>'
  };
  GOAL_ICONS._default = GOAL_ICONS.flag;

  function eachGoalBar(fn) { each(document.querySelectorAll("[data-lrc-mp-identity] .goalbar"), fn); }
  function captureGoalBaseline(bar) {
    if (!bar) return;
    if (goalEmptyHTML === null) goalEmptyHTML = bar.innerHTML;
    if (goalIcon === null) {
      var gt = bar.querySelector(".gt span");
      var txt = gt ? (gt.textContent || "") : "";
      goalIcon = (txt.trim().split(/\s+/)[0]) || ""; // leading token = emoji; fallback ''
    }
  }
  function dateOnlyMs(s) {
    var p = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(s || ""));
    if (!p) return null;
    return new Date(+p[1], (+p[2]) - 1, +p[3]).getTime();
  }
  function todayOnlyMs() { var n = new Date(); return new Date(n.getFullYear(), n.getMonth(), n.getDate()).getTime(); }
  function clamp(n, lo, hi) { return Math.max(lo, Math.min(hi, n)); }
  // Both types now carry trainingStart (start) + date (target): track fill runs
  // from start → target, and "days out" counts down to the target date.
  function renderGoalBar(goal) {
    eachGoalBar(function (bar) {
      captureGoalBaseline(bar);
      if (!goal || !goal.type || (goal.type !== "race" && goal.type !== "consistency")) {
        if (goalEmptyHTML !== null) bar.innerHTML = goalEmptyHTML; // restore empty state ('none'/absent)
        return;
      }
      var iconKey = (goal.icon && String(goal.icon).trim()) || goalIcon || "";
      var iconSvg = GOAL_ICONS[iconKey] || GOAL_ICONS._default || "";
      var label = (goal.type === "race")
        ? ((goal.name && String(goal.name).trim()) || "Race day")
        : ((goal.name && String(goal.name).trim()) || "Staying consistent");

      var startMs = dateOnlyMs(goal.trainingStart);
      var endMs = dateOnlyMs(goal.date);
      var gd = "", track = "";
      if (endMs != null) {
        var daysOut = Math.max(0, Math.round((endMs - todayOnlyMs()) / 86400000));
        gd = daysOut + " days out";
        var fill = 0;
        if (startMs != null && endMs > startMs) fill = clamp(((Date.now() - startMs) / (endMs - startMs)) * 100, 0, 100);
        track = '<div class="gtrack"><div class="gfill" style="width:' + fill + '%"></div></div>';
      }
      bar.innerHTML = '<div class="gt"><span class="gt-main">' + (iconSvg ? '<span class="gt-ic">' + iconSvg + '</span>' : '') + esc(label) + '</span><span class="gd">' + esc(gd) + '</span></div>' + track;
    });
  }
  // Name + training-start + target date show for BOTH types — no field show/hide.
  // The only type-specific bit is the target-date label.
  function setDateLabel(modal, type) {
    if (!modal) return;
    var lbl = modal.querySelector("[data-idn-datelabel]");
    if (lbl) lbl.textContent = (type === "consistency") ? "When Do You Want To Hit Your Goal?" : "When Is It?";
  }
  // Reflect the stored goal into an opening modal: select type pill, emoji, prefill
  // name/start/target, set the target-date label, show the remove link only when a
  // goal exists. A fresh modal (no stored goal) defaults to race.
  function applyGoalToModal(modal) {
    if (!modal) return;
    var g = storedGoal;
    var token = (g && g.type) ? (GOAL_REV[g.type] || null) : null;
    if (!token) token = "event"; // fresh → default race
    goalPickType = GOAL_CANON[token] || token;

    // TYPE pills
    each(modal.querySelectorAll(".qopts .qopt"), function (o) {
      var oc = o.getAttribute("onclick") || "";
      if (oc.indexOf("goalPick") === -1) return;
      o.classList.toggle("sel", oc.indexOf("'" + token + "'") > -1);
    });
    setDateLabel(modal, goalPickType);

    // emoji — match the stored icon, else default to the first
    var icon = (g && g.icon) ? String(g.icon).trim() : "";
    var emojis = modal.querySelectorAll('[data-idn-emoji] .emoji');
    var matchedEmoji = null;
    each(emojis, function (b) { if (!matchedEmoji && icon && (b.getAttribute("data-icon") || "").trim() === icon) matchedEmoji = b; });
    each(emojis, function (b) { b.classList.remove("sel"); });
    goalPickIcon = "";
    if (matchedEmoji) { matchedEmoji.classList.add("sel"); goalPickIcon = icon; }
    else if (emojis[0]) { emojis[0].classList.add("sel"); goalPickIcon = (emojis[0].getAttribute("data-icon") || "").trim(); }

    // name
    var nameInput = modal.querySelector('[data-idn-goalname] input[type="text"]');
    if (nameInput) nameInput.value = (g && g.name) ? g.name : "";

    // training-start + target date (both types)
    var dateInput = modal.querySelector('[data-idn-goaldate] input[type="date"]');
    if (dateInput) dateInput.value = (g && g.date) ? g.date : "";
    var tsInput = modal.querySelector('[data-idn-trainstart] input[type="date"]');
    if (tsInput) tsInput.value = (g && g.trainingStart) ? g.trainingStart : "";

    // remove link only when a goal exists
    var rm = modal.querySelector("[data-idn-goalremove]");
    if (rm) rm.style.display = (g && g.type && g.type !== "none") ? "block" : "none";
  }

  // ---- paginated goal flow ----
  function goalStepEls(scrim){ return scrim ? scrim.querySelectorAll(".goal-step") : []; }
  function goalCurStep(scrim){
    var steps = goalStepEls(scrim), i;
    for (i=0;i<steps.length;i++){ if (steps[i].classList.contains("is-active")) return i+1; }
    return 1;
  }
  function goalShowStep(scrim, n){
    if (!scrim) return;
    var steps = goalStepEls(scrim); var total = steps.length || 3;
    n = Math.max(1, Math.min(total, n));
    each(steps, function(s){ s.classList.toggle("is-active", +s.getAttribute("data-step") === n); });
    each(scrim.querySelectorAll("[data-idn-dots] .gdot"), function(d,i){ d.classList.toggle("is-on", i < n); });
    var back = scrim.querySelector("[data-idn-goalback]");
    var next = scrim.querySelector("[data-idn-goalnext]");
    var save = scrim.querySelector("[data-idn-goalsave]");
    var nav = scrim.querySelector(".goal-nav");
    if (nav) nav.classList.toggle("is-step1", n === 1);
    if (back) back.style.visibility = (n > 1) ? "visible" : "hidden";
    var last = (n >= total);
    if (next) next.style.display = last ? "none" : "";
    if (save) save.style.display = last ? "block" : "none";
    goalValidateStep(scrim, n);
  }
  // per-step gate: step1 needs a type pill, step2 needs a name, step3 handled by saveGoal
  function goalValidateStep(scrim, n){
    var next = scrim.querySelector("[data-idn-goalnext]"); if (!next) return;
    var ok = true;
    if (n === 1){ ok = !!scrim.querySelector(".qopts .qopt.sel"); }
    else if (n === 2){ var ni = scrim.querySelector('[data-idn-goalname] input[type="text"]'); ok = !!(ni && ni.value.trim()); }
    next.disabled = !ok;
  }

  var api = {
    goalStep: function (el, dir) {
      var scrim = el.closest(".scrim"); if (!scrim) return;
      var cur = goalCurStep(scrim);
      if (dir > 0) goalValidateStep(scrim, cur);
      var next = scrim.querySelector("[data-idn-goalnext]");
      if (dir > 0 && next && next.disabled) return; // gate forward nav
      goalShowStep(scrim, cur + dir);
    },
    openGoal: function (el) {
      var sec = el.closest(".idn");
      // the scrim starts in the card but, once portaled, lives in the wrapper; some
      // close paths (saveGoal/removeGoal/backdrop) don't restore it, so look in both
      var m = (sec && sec.querySelector('[data-idn-modal="goal"]')) || (window.__lrcGoalPortal && window.__lrcGoalPortal.querySelector('[data-idn-modal="goal"]'));
      if (!m) return;
      // Portal into a body-level .idn wrapper: position:fixed then works (no
      // transformed ancestor), AND every .idn-scoped modal rule still matches
      // because the scrim is still inside an .idn. Remember origin to restore.
      if (m.parentNode !== window.__lrcGoalPortal) {
        if (!window.__lrcGoalPortal) {
          var p = document.createElement("div");
          p.className = "idn lrc-goal-portal";
          document.body.appendChild(p);
          window.__lrcGoalPortal = p;
        }
        m.__lrcHome = m.parentNode;
        m.__lrcNext = m.nextSibling;
        window.__lrcGoalPortal.appendChild(m);
      }
      applyGoalToModal(m);          // reflect the stored goal each time it opens
      goalShowStep(m, 1);           // always start at step 1
      m.classList.add("show");
      try {
        document.documentElement.style.overflow = "hidden";
        document.body.style.overflow = "hidden";
        document.documentElement.style.height = "100%";
        document.body.style.height = "100%";
      } catch (e) {}
    },
    _openGoalLegacy: function (el) {
      var sec = el.closest(".idn");
      var m = sec && sec.querySelector('[data-idn-modal="goal"]');
      if (!m) return;
      applyGoalToModal(m);          // reflect the stored goal each time it opens
      m.classList.add("show");
    },
    modalClose: function (el) {
      var m = el.closest(".scrim"); if (!m) return;
      m.classList.remove("show");
      if (m.getAttribute("data-idn-modal") === "goal") {
        try {
          document.documentElement.style.overflow = "";
          document.body.style.overflow = "";
          document.documentElement.style.height = "";
          document.body.style.height = "";
        } catch (e) {}
      }
      if (m.__lrcHome) {
        try { if (m.__lrcNext && m.__lrcNext.parentNode === m.__lrcHome) m.__lrcHome.insertBefore(m, m.__lrcNext); else m.__lrcHome.appendChild(m); } catch (e) {}
        m.__lrcHome = null; m.__lrcNext = null;
      }
    },
    goalPick: function (el, type) {
      each(el.parentElement.querySelectorAll(".qopt"), function (o) { o.classList.remove("sel"); });
      el.classList.add("sel");
      goalPickType = GOAL_CANON[type] || type;      // remember the canonical selection
      setDateLabel(el.closest(".scrim"), goalPickType); // race vs. consistency target-date wording
      var scrim = el.closest(".scrim"); if (scrim) goalValidateStep(scrim, 1);
    },
    goalEmoji: function (el) {
      var wrap = (el.closest && el.closest("[data-idn-emoji]")) || el.parentElement;
      each(wrap.querySelectorAll(".emoji"), function (o) { o.classList.remove("sel"); });
      el.classList.add("sel");
      goalPickIcon = (el.getAttribute("data-icon") || "").trim();
    },
    saveGoal: function (el) {
      if (causeDesignMode(el)) return;          // never persist from the theme editor
      var modal = el.closest(".scrim");
      var type = goalPickType;
      if (!type) return;
      // BOTH dates are required (start + target). If either is empty, do nothing.
      var tsInput = modal && modal.querySelector('[data-idn-trainstart] input[type="date"]');
      var dateInput = modal && modal.querySelector('[data-idn-goaldate] input[type="date"]');
      var trainingStart = (tsInput && tsInput.value) ? tsInput.value : null;
      var dateVal = (dateInput && dateInput.value) ? dateInput.value : null;
      if (!trainingStart || !dateVal) return;   // inline-validate: need start AND target
      var nameInput = modal && modal.querySelector('[data-idn-goalname] input[type="text"]');
      var name = (nameInput && nameInput.value) ? nameInput.value.trim() : "";
      // icon: the modal selection, falling back to whichever emoji is marked .sel
      var icon = goalPickIcon;
      if (!icon && modal) { var ei = modal.querySelector('[data-idn-emoji] .emoji.sel') || modal.querySelector('[data-idn-emoji] .emoji'); icon = ei ? (ei.getAttribute("data-icon") || "").trim() : ""; }
      var goal = { type: type, name: name, icon: icon || "", trainingStart: trainingStart, date: dateVal };
      fetch('/apps/lrc', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ action: 'save_goal', goal: goal }) })
        .then(function (r) { return r.json(); })
        .then(function (j) {
          if (!j || !j.ok) return;
          // prefer the server's stored goal (carries setAt) for an accurate track fill
          storedGoal = (j.member && j.member.goal) ? j.member.goal : goal;
          renderGoalBar(storedGoal);
          if (modal) modal.classList.remove("show");
          /* onboarding: backend stamped the 'event' item inside save_goal (real goal only); tell the checklist to re-read */
          try { document.dispatchEvent(new CustomEvent("lrc:onboarding-changed")); } catch (e) {}
        })
        .catch(function () {});
    },
    removeGoal: function (el) {
      if (causeDesignMode(el)) return;
      var modal = el.closest(".scrim");
      fetch('/apps/lrc', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ action: 'save_goal', goal: { type: 'none' } }) })
        .then(function (r) { return r.json(); })
        .then(function (j) {
          if (!j || !j.ok) return;
          storedGoal = null;
          renderGoalBar(null);
          if (modal) modal.classList.remove("show");
        })
        .catch(function () {});
    },
    pickCause: function (el) {
      var wrap = el.closest(".causes"); if (!wrap) return;
      each(wrap.querySelectorAll(".cause"), function (c) { c.classList.remove("sel"); });
      el.classList.add("sel"); // visual selection
      setCauseHeaderIcon(el, causeValue(el));   // collapsed header reflects the choice
      // Persist the choice (fire-and-forget) — but never from the theme editor.
      if (causeDesignMode(el)) return;
      var chosen = causeValue(el);
      if (!chosen) return;
      storedCause = chosen;
      /* onboarding: backend stamps the 'cause' item inside save_cause; dispatch AFTER the fetch resolves so the checklist re-read sees the committed stamp */
      fetch('/apps/lrc', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ action: 'save_cause', cause: chosen }) }).then(function () { try { document.dispatchEvent(new CustomEvent("lrc:onboarding-changed")); } catch (e) {} }).catch(function () {});
    },
    rate: function (el) {
      var bar = el.closest("[data-idn-rate]"); if (!bar) return;
      var m = bar.querySelector("[data-idn-ratemsg]"); if (!m) return;
      var v = +el.value, low = +(bar.getAttribute("data-low") || 3);
      var mode = (v <= low) ? "low" : "high";
      if (bar.getAttribute("data-mode") === mode) return;
      bar.setAttribute("data-mode", mode);
      if (mode === "low") {
        m.className = "hratemsg low";
        var ph = esc(bar.getAttribute("data-commentph") || "What went wrong?");
        var sendLabel = esc(bar.getAttribute("data-sendlabel") || "Send");
        m.innerHTML = '<div class="hratemsg__msg">' + esc(bar.getAttribute("data-lowmsg") || "") + '</div>'
          + '<textarea class="hrate-note" data-idn-ratenote rows="2" placeholder="' + ph + '"></textarea>'
          + '<button type="button" class="btn btn-primary review-cta" onclick="LRCMPI.submitReview(this)">' + sendLabel + '</button>';
      } else {
        m.className = "hratemsg high";
        if (_hasReviewed) {
          m.innerHTML = '<div class="hratemsg__msg">' + esc(bar.getAttribute("data-reviewedmsg") || "Awesome — we’ll keep at it.") + '</div>';
        } else {
          var pts = bar.getAttribute("data-points") || "";
          var label = (bar.getAttribute("data-review") || "Leave a review").replace("{points}", pts);
          m.innerHTML = '<div class="hratemsg__msg">' + esc(bar.getAttribute("data-highmsg") || "") + '</div>'
            + '<button type="button" class="btn btn-primary review-cta" onclick="LRCMPI.openReviewForm(this)">' + esc(label) + '</button>';
        }
      }
    },
    _reviewPost: function (bar, payload) {
      try { if (window.Shopify && window.Shopify.designMode) return; } catch (e) {}
      try { if (bar && bar.getAttribute("data-design-mode") === "true") return; } catch (e) {}
      try { fetch("/apps/lrc", { method: "POST", credentials: "same-origin", headers: { "Content-Type": "application/json" }, body: JSON.stringify(payload) }).catch(function () {}); } catch (e) {}
    },
    submitReview: function (btn) {
      var bar = btn.closest("[data-idn-rate]"); if (!bar) return;
      var slider = bar.querySelector(".hslider"); var note = bar.querySelector("[data-idn-ratenote]");
      LRCMPI._reviewPost(bar, { action: "save_review", kind: "negative", rating: slider ? +slider.value : null, comment: note ? note.value : "" });
      var m = bar.querySelector("[data-idn-ratemsg]");
      if (m) { m.className = "hratemsg low"; m.innerHTML = '<div class="hratemsg__msg">' + esc(bar.getAttribute("data-negconfirm") || "Thanks — we’ve flagged this.") + '</div>'; }
    },
    openReviewForm: function (btn) {
      var sec = btn.closest(".idn"); if (!sec) return;
      var modal = sec.querySelector('[data-idn-modal="review"]'); if (!modal) return;
      // stars: pre-select all 5 on open
      var starsWrap = modal.querySelector("[data-rev-stars]");
      if (starsWrap) {
        starsWrap.setAttribute("data-rating", 5);
        each(starsWrap.querySelectorAll(".hrev-starbtn"), function (b) { b.classList.add("is-on"); });
      }
      // category default coffee
      var cats = modal.querySelector("[data-rev-cats]");
      if (cats) each(cats.querySelectorAll(".hrev-cat"), function (b) { b.classList.toggle("is-on", b.getAttribute("data-rev-cat") === "coffee"); });
      // clear inputs + error
      var t = modal.querySelector("[data-rev-title]"); if (t) t.value = "";
      var bd = modal.querySelector("[data-rev-body]"); if (bd) bd.value = "";
      var err = modal.querySelector("[data-rev-error]"); if (err) err.setAttribute("hidden", "");
      // reset the submit button (in case a prior attempt left it disabled)
      var sub = modal.querySelector(".btn.btn-primary"); if (sub) { sub.disabled = false; sub.textContent = modal.getAttribute("data-submitlabel") || "Submit review"; }
      // reset-on-open: always show the form, never a stale confirmation (immune to which close path was used)
      var cf = modal.querySelector("[data-rev-conf]"); if (cf) cf.setAttribute("hidden", "");
      var ff = modal.querySelector("[data-rev-form]"); if (ff) ff.removeAttribute("hidden");
      modal.classList.add("show");
    },
    pickStar: function (el) {
      var wrap = el.closest("[data-rev-stars]"); if (!wrap) return;
      var r = +el.getAttribute("data-star");
      wrap.setAttribute("data-rating", r);
      each(wrap.querySelectorAll(".hrev-starbtn"), function (b) { b.classList.toggle("is-on", (+b.getAttribute("data-star")) <= r); });
    },
    pickCat: function (btn) {
      var wrap = btn.closest("[data-rev-cats]"); if (!wrap) return;
      each(wrap.querySelectorAll(".hrev-cat"), function (b) { b.classList.remove("is-on"); });
      btn.classList.add("is-on");
    },
    submitProductReview: function (btn) {
      var modal = btn.closest('[data-idn-modal="review"]'); if (!modal) return;
      var starsWrap = modal.querySelector("[data-rev-stars]");
      var rating = starsWrap ? (+starsWrap.getAttribute("data-rating") || 5) : 5;
      var catEl = modal.querySelector("[data-rev-cat].is-on");
      var category = catEl ? catEl.getAttribute("data-rev-cat") : "coffee";
      var titleEl = modal.querySelector("[data-rev-title]"), bodyEl = modal.querySelector("[data-rev-body]"), errEl = modal.querySelector("[data-rev-error]");
      var title = titleEl ? titleEl.value.trim() : "", body = bodyEl ? bodyEl.value.trim() : "";
      if (!rating || !title || !body) { if (errEl) errEl.removeAttribute("hidden"); return; }
      if (errEl) errEl.setAttribute("hidden", "");
      btn.disabled = true; btn.textContent = "Submitting…";
      var sec = modal.closest(".idn");
      function done(){
        _hasReviewed = true;
        // keep the modal OPEN; swap the form for the confirmation screen (closed via its Done button → modalClose)
        var formEl = modal.querySelector("[data-rev-form]"); if (formEl) formEl.setAttribute("hidden", "");
        var confEl = modal.querySelector("[data-rev-conf]"); if (confEl) confEl.removeAttribute("hidden");
        var bar = sec && sec.querySelector("[data-idn-rate]");
        var m = bar && bar.querySelector("[data-idn-ratemsg]");
        if (m) { m.className = "hratemsg high"; m.innerHTML = '<div class="hratemsg__msg">' + esc(bar.getAttribute("data-reviewedmsg") || "Awesome — we’ll keep at it.") + '</div>'; }
        try { document.dispatchEvent(new CustomEvent('lrc:review-state', { detail: { hasReviewed: true } })); } catch (e) {}
      }
      try { if (window.Shopify && window.Shopify.designMode) { done(); return; } } catch (e) {}
      fetch("/apps/lrc", { method: "POST", credentials: "same-origin", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ action: "submit_review", rating: rating, category: category, title: title, body: body, name: _firstName || "" }) })
        .then(function () { done(); }).catch(function () { done(); });
    }
  };
  // expose cause helpers + stored value for the on-load hydrate
  api.selectCauseIn = selectCauseIn;
  api.causeValue = causeValue;
  api.setStoredCause = function (v) { storedCause = v; };
  api.getStoredCause = function () { return storedCause; };
  // expose goal helpers for the on-load hydrate
  api.renderGoalBar = renderGoalBar;
  api.setStoredGoal = function (v) { storedGoal = v; };
  api.getStoredGoal = function () { return storedGoal; };
  // review-state getter — load-order fallback so the rewards pill can read state at boot
  api.hasReviewed = function () { return _hasReviewed; };
  // close modal when the scrim backdrop itself is clicked
  document.addEventListener("click", function (e) {
    if (e.target && e.target.classList && e.target.classList.contains("scrim")) e.target.classList.remove("show");
  });
  // re-validate step 2 Next as the name field is typed
  document.addEventListener("input", function (e) {
    var ni = e.target && e.target.closest ? e.target.closest('[data-idn-goalname]') : null;
    if (!ni) return;
    var scrim = e.target.closest(".scrim"); if (scrim) { try { var n = 0; var steps = scrim.querySelectorAll(".goal-step"); for (var i=0;i<steps.length;i++){ if (steps[i].classList.contains("is-active")) n = i+1; } if (n) { var nx = scrim.querySelector("[data-idn-goalnext]"); var v = ni.querySelector('input'); if (nx && n===2) nx.disabled = !(v && v.value.trim()); } } catch(err){} }
  });
  return api;
})();

(function () {
  "use strict";
  var roots = document.querySelectorAll("[data-lrc-mp-identity]");
  if (!roots.length) return;

  // ---- "You've saved" baseline: prefer the STORED locked value; the live estimate
  //      only ever locks the baseline once, on first view. ----
  var _storedSavings;            // undefined until get_member resolves; then a number or null
  var _freshSavings;             // the live estimate from the publish payload
  var _savingsLockPosted = false;
  function _fmtUSD(n){ return '$' + Number(n).toLocaleString('en-US',{minimumFractionDigits:2,maximumFractionDigits:2}); }
  function _liveRoots(){ return Array.prototype.filter.call(roots, function(r){ return r.getAttribute('data-design-mode') !== 'true'; }); }
  function _reconcileSavings(){
    var useStored = (typeof _storedSavings === 'number' && _storedSavings > 0);
    var val = useStored ? _storedSavings : ((typeof _freshSavings === 'number' && _freshSavings > 0) ? _freshSavings : null);
    if (val != null) _liveRoots().forEach(function(r){ var n=r.querySelector('[data-idn="saved"]'); if(n) n.textContent = _fmtUSD(val); });
    if (!_savingsLockPosted && _storedSavings !== undefined && !useStored && typeof _freshSavings === 'number' && _freshSavings > 0) {
      _savingsLockPosted = true;
      fetch('/apps/lrc',{method:'POST',credentials:'same-origin',headers:{'Content-Type':'application/json'},body:JSON.stringify({action:'save_savings',amount:_freshSavings})}).catch(function(){});
    }
  }

  Array.prototype.forEach.call(roots, function (root) {
    var DESIGN = root.getAttribute("data-design-mode") === "true" ||
                 (typeof window.Shopify !== "undefined" && window.Shopify && window.Shopify.designMode === true);
    var eyebrowLabel = root.getAttribute("data-eyebrow-label") || "Member · since";
    var greetTpl = root.getAttribute("data-greeting-template") || "Run strong, {name}.";

    function q(name) { return root.querySelector('[data-idn="' + name + '"]'); }
    function greet(name) {
      return greetTpl.replace("{name}", name || "")
        .replace(/^\s*,\s*/, "")               // stray leading comma when name empty
        .replace(/,\s*([.!?])\s*$/, "$1")       // stray comma before end punctuation
        .replace(/\s{2,}/g, " ").trim();
    }
    function render(d) {
      if (!d) return;
      if (d.state === "signedout" || d.state === "error" || d.state === "empty") { root.hidden = true; return; }
      root.hidden = false;
      if (typeof d.firstName === 'string') _firstName = d.firstName; // shared with LRCMPI.submitProductReview
      var since = d.memberSinceText || "";
      // Label already ends in "since" — join with a space, not another separator.
      if (q("eyebrow")) q("eyebrow").textContent = since ? (eyebrowLabel + " " + since) : eyebrowLabel;
      if (q("name")) q("name").textContent = greet(d.firstName);
      // Days: fill from the payload when present (leave the — placeholder if absent).
      if (q("days") && d.daysFueled != null) q("days").textContent = String(d.daysFueled);
      // Savings: don't write directly — feed the live estimate into the reconciler,
      // which prefers the stored locked baseline and locks on first view.
      if (typeof d.savedAmount === 'number') _freshSavings = d.savedAmount;
      _reconcileSavings();
    }

    if (DESIGN) {
      render({ state: "ready", firstName: "Thomas", memberSinceText: "May 2025", daysFueled: 238, savedText: "$11.99" });
      return;
    }

    // Live: read the latest published payload if present, and update on each event.
    if (window.LRCMP_DATA) render(window.LRCMP_DATA);
    document.addEventListener("lrc-mp:identity", function (e) { render(e.detail); });
  });

  // One-time hydrate of the saved cause (mirrors the get_member fetch used by
  // lrc-mp-forecast.js / lrc-mp-quiz.js). Skipped entirely in the theme editor.
  // The cause-bar markup is static Liquid that render() never rebuilds, so a
  // single pre-select on load is enough — no per-event re-apply required.
  (function () {
    var live = Array.prototype.filter.call(roots, function (r) { return r.getAttribute("data-design-mode") !== "true"; });
    var design = (typeof window.Shopify !== "undefined" && window.Shopify && window.Shopify.designMode === true);
    if (design || !live.length) return;

    // NOTE: the "Set my goal" button now has an inline onclick="LRCMPI.saveGoal(this)"
    // (and Remove uses onclick="LRCMPI.removeGoal(this)"), so no addEventListener binding
    // here — binding both would double-fire the save.

    fetch("/apps/lrc", {
      method: "POST", credentials: "same-origin",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ action: "get_member" })
    })
      .then(function (r) { return r.json(); })
      .then(function (j) {
        if (!j || j.ok !== true || !j.member) return;
        var cause = j.member.cause;
        if (typeof cause === "string" && cause.trim()) {
          cause = cause.trim();
          if (window.LRCMPI && window.LRCMPI.setStoredCause) window.LRCMPI.setStoredCause(cause);
          live.forEach(function (r) { if (window.LRCMPI && window.LRCMPI.selectCauseIn) window.LRCMPI.selectCauseIn(r, cause); });
        }
        var goal = j.member.goal;
        if (goal && goal.type && goal.type !== "none") {
          if (window.LRCMPI && window.LRCMPI.setStoredGoal) window.LRCMPI.setStoredGoal(goal);
          if (window.LRCMPI && window.LRCMPI.renderGoalBar) window.LRCMPI.renderGoalBar(goal);
        }
        _storedSavings = (j && j.member && typeof j.member.lifetimeSavings === 'number') ? j.member.lifetimeSavings : null;
        _reconcileSavings();
        // Review bar stays visible always. Only the positive CTA suppresses (→ thank-you)
        // once the member has left a review; the negative comment path is always available.
        _hasReviewed = !!(j.member && j.member.hasReviewed);
        try { document.dispatchEvent(new CustomEvent('lrc:review-state', { detail: { hasReviewed: _hasReviewed } })); } catch (e) {}
        live.forEach(function (r) { var s = r.querySelector("[data-idn-rate] .hslider"); if (s && window.LRCMPI && LRCMPI.rate) LRCMPI.rate(s); });
      })
      .catch(function () {});
  })();
})();

/* ===== Pass 9: iOS review-modal scroll-lock + keyboard focus-scroll ===== */
(function () {
  function init() {
    var scrim = document.querySelector('[data-idn-modal="review"]');
    if (!scrim) return;
    var sheet = scrim.querySelector(".sheet");
    var locked = false, savedY = 0;

    function lock() {
      if (locked) return; locked = true;
      savedY = window.pageYOffset || document.documentElement.scrollTop || 0;
      document.body.style.position = "fixed";
      document.body.style.top = (-savedY) + "px";
      document.body.style.left = "0";
      document.body.style.right = "0";
      document.body.style.width = "100%";
    }
    function unlock() {
      if (!locked) return; locked = false;
      document.body.style.position = "";
      document.body.style.top = "";
      document.body.style.left = "";
      document.body.style.right = "";
      document.body.style.width = "";
      window.scrollTo(0, savedY);
    }
    function onShow() {
      lock();
      if (sheet) sheet.scrollTop = 0;
    }

    var obs = new MutationObserver(function () {
      if (scrim.classList.contains("show")) { if (!locked) onShow(); }
      else { if (locked) unlock(); }
    });
    obs.observe(scrim, { attributes: true, attributeFilter: ["class"] });

    /* when a field is focused, scroll it toward the top of the sheet so the keyboard can't cover it */
    if (sheet) {
      sheet.addEventListener("focusin", function (e) {
        var t = e.target;
        if (!t || (t.tagName !== "INPUT" && t.tagName !== "TEXTAREA")) return;
        setTimeout(function () {
          try {
            var sr = sheet.getBoundingClientRect(), tr = t.getBoundingClientRect();
            sheet.scrollTop += (tr.top - sr.top) - 16;
          } catch (err) {}
        }, 250);   /* wait for the keyboard to animate in before measuring */
      });
    }
  }
  if (document.readyState === "loading") document.addEventListener("DOMContentLoaded", init);
  else init();
})();

/* ===== Pass B2-fix2: visualViewport keyboard pin for the goal modal =====
   Definitive iOS fix — tracks the visual viewport (which shrinks when the
   keyboard opens) and sizes/positions the scrim to it, so the page can never
   show above/below the sheet. No position:fixed body-lock (that desyncs iOS
   hit-testing). */
(function () {
  var vv = window.visualViewport;
  function portal(){ return window.__lrcGoalPortal; }
  function scrim(){ var p = portal(); return p ? p.querySelector('[data-idn-modal="goal"]') : null; }
  function pin(){
    var s = scrim(); if (!s || !s.classList.contains("show")) return;
    var p = portal(); if (!p) return;
    if (vv){
      p.style.setProperty("--idn-vvh", vv.height + "px");
      p.style.setProperty("--idn-vvy", (vv.offsetTop || 0) + "px");
      var kbh = Math.max(0, window.innerHeight - vv.height - (vv.offsetTop || 0));
      p.style.setProperty("--idn-kbh", kbh + "px");
    } else {
      p.style.setProperty("--idn-vvh", window.innerHeight + "px");
      p.style.setProperty("--idn-vvy", "0px");
    }
  }
  function clear(){
    var p = portal(); if (!p) return;
    p.style.removeProperty("--idn-vvh");
    p.style.removeProperty("--idn-vvy");
    p.style.removeProperty("--idn-kbh");
  }
  if (vv){
    vv.addEventListener("resize", pin);
    vv.addEventListener("scroll", pin);
  }
  // watch the portal for the scrim gaining/losing .show
  function watch(){
    var p = portal(); if (!p) { setTimeout(watch, 400); return; }
    var obs = new MutationObserver(function(){
      var s = scrim();
      if (s && s.classList.contains("show")) { pin(); }
      else {
        clear();
        // release the html/body scroll lock for EVERY close path — saveGoal,
        // removeGoal and the backdrop click bypass modalClose, so without this
        // the page would stay overflow:hidden (frozen) after saving a goal.
        try {
          document.documentElement.style.overflow = "";
          document.body.style.overflow = "";
          document.documentElement.style.height = "";
          document.body.style.height = "";
        } catch (e) {}
      }
    });
    obs.observe(p, { attributes:true, subtree:true, attributeFilter:["class"] });
  }
  // the portal is created lazily on first openGoal; poll until it exists
  watch();
})();
